/**
 * ActionsSection.js — Section 2 wrapper for the Mechanic Composer
 *
 * Renders:
 * - Action tabs at top: clickable tabs for each action + [+] button
 * - Active tab has red underline indicator
 * - The selected action's ActionPipeline
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import ActionPipeline from '../pipeline/ActionPipeline';

const ActionsSection = ({ config, dispatch, errors }) => {
  const actions = config?.actions || [];
  const [activeTab, setActiveTab] = useState(0);

  // Keep activeTab in bounds
  const safeActiveTab = actions.length === 0 ? 0 : Math.min(activeTab, actions.length - 1);

  const handleAddAction = () => {
    dispatch({ type: 'ADD_ACTION' });
    // Switch to the new tab
    setActiveTab(actions.length);
  };

  const handleDeleteAction = (index) => {
    // ActionPipeline dispatches DELETE_ACTION; we adjust activeTab
    if (safeActiveTab >= actions.length - 1 && safeActiveTab > 0) {
      setActiveTab(safeActiveTab - 1);
    }
  };

  const truncateLabel = (label, max = 20) => {
    if (!label) return 'Untitled';
    return label.length > max ? label.slice(0, max) + '...' : label;
  };

  // Wrap dispatch to intercept DELETE_ACTION for tab management
  const wrappedDispatch = (actionObj) => {
    if (actionObj.type === 'DELETE_ACTION') {
      handleDeleteAction(actionObj.actionIndex);
    }
    dispatch(actionObj);
  };

  return (
    <div>
      {/* Action Tabs */}
      <div role="tablist" aria-label="Action tabs" className="flex items-center gap-1 mb-4 border-b border-gray-700 pb-0.5 overflow-x-auto">
        {actions.map((action, index) => (
          <button
            key={action.id || index}
            type="button"
            role="tab"
            aria-selected={index === safeActiveTab}
            aria-controls={`action-panel-${index}`}
            id={`action-tab-${index}`}
            onClick={() => setActiveTab(index)}
            className={cn(
              'px-3 py-1.5 text-sm rounded-t-md transition-colors whitespace-nowrap',
              'hover:bg-gray-800/50',
              index === safeActiveTab
                ? 'text-white border-b-2 border-red-500 font-medium'
                : 'text-gray-400'
            )}
          >
            {truncateLabel(action.label)}
          </button>
        ))}
        <button
          type="button"
          onClick={handleAddAction}
          className={cn(
            'px-2 py-1.5 text-sm text-gray-400 hover:text-white',
            'hover:bg-gray-800/50 rounded-t-md transition-colors'
          )}
          title="Add Action"
          aria-label="Add Action"
        >
          <Plus size={16} />
        </button>
      </div>

      {/* Active Action Pipeline */}
      {actions.length > 0 && actions[safeActiveTab] ? (
        <div
          role="tabpanel"
          id={`action-panel-${safeActiveTab}`}
          aria-labelledby={`action-tab-${safeActiveTab}`}
        >
        <ActionPipeline
          action={actions[safeActiveTab]}
          actionIndex={safeActiveTab}
          dispatch={wrappedDispatch}
          errors={errors?.[safeActiveTab]}
        />
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm mb-1">Actions define what your mechanic does when activated.</p>
          <p className="text-xs text-gray-600 mb-3">Each action can have conditions (WHEN), costs (PAY), and effects (THEN).</p>
          <button
            type="button"
            onClick={handleAddAction}
            className={cn(
              'inline-flex items-center gap-1 px-4 py-2 text-sm rounded-md',
              'border border-dashed border-gray-600 text-gray-400',
              'hover:border-red-500 hover:text-red-400 transition-colors'
            )}
          >
            <Plus size={14} />
            Add First Action
          </button>
        </div>
      )}
    </div>
  );
};

export default ActionsSection;
